import styled from 'styled-components'
import Text from '@/components/common/Text'
import Modal from '@/components/common/Modal'
import { device } from '@/common/constants/device'
import Row from '@/components/common/Row'
import React, { useEffect, useState } from 'react'
import BaseButtonClose from '@/components/common/BaseButtonClose'
import BaseTextArea from '@/components/common/BaseTextArea'
import Button from '@/components/ui/Button'
import { useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import { close } from '@/store/modalsSlice'
import { requiredRule } from '@/common/validationRules'
import classNames from 'classnames'
import baseLogger from '@/common/services/BaseLogger'
import { userApi } from '@/common/services/api'
import { addNotification } from '@/store/notificationsSlice'
import { useTranslations } from 'next-intl'

const FeatureRequestModal = styled(Modal)`
  .modal-content {
    width: calc(100% - 30px);

    @media ${device.tablet} {
      width: 100%;
    }
  }
`

const FeatureRequestModalContent = styled.form`
  position: relative;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  width: 100%;
`

const FeatureRequestModalRow = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 12px;
`

const FeatureRequestModalTop = styled(Row)`
  justify-content: space-between;
  column-gap: 12px;

  @media ${device.tablet} {
    align-items: center;
  }
`

const FeatureRequestModalButton = styled(Button)`
  width: 100%;
`

const FeatureRequestModalArea = styled(BaseTextArea)`
  &.error {
    border-color: var(--red);
  }
`

const ProfileFeatureRequestModal = () => {
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useDispatch()
  const t = useTranslations('common')

  const closeModal = () => {
    dispatch(close())
  }

  const {
    register,
    watch,
    trigger,
    formState: { errors },
    clearErrors,
    setValue,
  } = useForm({
    defaultValues: {
      featureRequest: '',
      featureBenefit: '',
      userOccupation: '',
    },
  })
  const { featureRequest, featureBenefit, userOccupation } = watch()

  useEffect(() => {
    register('featureRequest', { required: requiredRule })
  }, [register])

  const getPayload = () => {
    return {
      survey: 'features_survey',
      responses: [
        {
          question: 'featureRequest',
          answers: [featureRequest],
        },
        {
          question: 'featureBenefit',
          answers: [featureBenefit],
        },
        {
          question: 'featureBenefit',
          answers: [featureBenefit],
        },
      ],
    }
  }

  const onSubmit = async (e) => {
    try {
      e.preventDefault()

      const isValid = await trigger()

      if (!isValid) return

      setIsLoading(true)
      const payload = getPayload()
      await userApi.setFeedback({
        data: payload,
      })
      setIsLoading(false)

      dispatch(
        addNotification({
          type: 'success',
          text: 'common.featureRequestNotification',
        })
      )

      closeModal()
    } catch (e) {
      baseLogger.logger.error(e, 'failed fetch user feature')
    } finally {
      setIsLoading(false)
    }
  }

  const fieldClasses = (field) => {
    return classNames({
      error: errors[field],
    })
  }

  return (
    <FeatureRequestModal maxWidth="644" needOffsetHeader onClose={closeModal}>
      <FeatureRequestModalContent onSubmit={onSubmit} noValidate>
        <FeatureRequestModalTop>
          <Text size="20" color="black" weight="bold">
            {t('featureRequestTitle')}
          </Text>
          <BaseButtonClose close={closeModal} />
        </FeatureRequestModalTop>
        <FeatureRequestModalRow>
          <Text size="16">{t('featureRequestText')}</Text>
          <FeatureRequestModalArea
            value={featureRequest}
            className={fieldClasses('featureRequest')}
            onChange={(e) => setValue('featureRequest', e.target.value)}
            onClick={() => clearErrors('featureRequest')}
            name="feature_request"
            cols="10"
          />
          {errors.featureRequest && (
            <Text size="14" color="red">
              {errors.featureRequest.message}
            </Text>
          )}
        </FeatureRequestModalRow>
        <FeatureRequestModalRow>
          <Text size="16">{t('featureRequestText2')}</Text>
          <FeatureRequestModalArea
            onClick={() => clearErrors('featureBenefit')}
            value={featureBenefit}
            className={fieldClasses('featureBenefit')}
            onChange={(e) => setValue('featureBenefit', e.target.value)}
            name="feature_benefit"
            cols="10"
          />
        </FeatureRequestModalRow>
        <FeatureRequestModalRow>
          <Text size="16">{t('featureRequestText3')}</Text>
          <FeatureRequestModalArea
            onClick={() => clearErrors('userOccupation')}
            value={userOccupation}
            className={fieldClasses('userOccupation')}
            onChange={(e) => setValue('userOccupation', e.target.value)}
            name="user_occupation"
            cols="10"
          />
        </FeatureRequestModalRow>
        <FeatureRequestModalButton isLoading={isLoading} filled>
          {t('send')}
        </FeatureRequestModalButton>
      </FeatureRequestModalContent>
    </FeatureRequestModal>
  )
}

export default ProfileFeatureRequestModal
